// The identity page's faces card, connected (PRD-0031 slice 01).
//
// The card reads confirmed faces and reuses the reference mutation; the main
// photo call updates the header avatar. Suggestions live in IdentityMatches.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { FacesCardActions, FacesCardState } from '@/components/faces/FacesCard'
import { mainFaceId as resolveMainFaceId } from '@/components/faces/faces-card'
import type { FaceMatchingIdentityDetail } from '@/services/api/face-matching'
import { identitiesApi } from '@/services/api/identities'
import { canDecideFaces } from '../decision-role'
import { confirmedFaceErrorKey, latestRefusal } from '../errors'
import { useFaceEvidenceGraph } from './useFaceEvidenceGraph'
import {
  identityQuery,
  invalidateFaceMatching,
  useFaceMatchingIdentity,
  useFaceReferenceToggle,
} from './useFaceMatching'

/**
 * The detail query includes the complete reference set. Exported for the page,
 * which watches the same read for an identity that was merged away (ADR-0053).
 */
export const CARD_DETAIL_OPTIONS = { pageSize: 12 } as const

export interface UseIdentityFacesResult {
  detail: FaceMatchingIdentityDetail | undefined
  state: FacesCardState
  actions: FacesCardActions
}

/**
 * Wire the faces card of one identity. *onMainPhotoChanged* lets the page
 * reload the identity itself, so the header avatar follows the new pick
 * without a page reload; the list and graph read `photo_path` and pick it up
 * on their own next read.
 *
 * A viewer gets neither the reference toggle nor the main photo pick (PRD-0032
 * SC-036): the read names the role, and the card says why. A refused change —
 * the 403 of a role that changed since the read, the 409 or the 404 of a face
 * that is no longer this identity's, or a plain failure — is the card's
 * `errorKey`.
 */
export function useIdentityFaces(
  caseId: string,
  personId: string | null,
  onMainPhotoChanged?: () => void
): UseIdentityFacesResult {
  const queryClient = useQueryClient()
  const evidence = useFaceEvidenceGraph(caseId, personId)
  const queries = [identityQuery(CARD_DETAIL_OPTIONS)]
  const detailQuery = useFaceMatchingIdentity(caseId, personId, CARD_DETAIL_OPTIONS)
  const reference = useFaceReferenceToggle(caseId, personId, queries)
  // The API owns the default (identity-document face, else first reference); we
  // only remember an explicit pick made here, so the badge moves at once.
  const [chosenFaceId, setChosenFaceId] = useState<string | null>(null)

  const mainPhoto = useMutation({
    mutationFn: (faceId: string) => identitiesApi.setMainPhoto(caseId, personId as string, faceId),
    onSuccess: result => {
      setChosenFaceId(result.face_id)
      onMainPhotoChanged?.()
    },
    // Refused or not, the card is read again: a face that is no longer this
    // identity's leaves it, and a role that changed takes the controls with it.
    onSettled: () => invalidateFaceMatching(queryClient, caseId),
  })

  const canDecide = canDecideFaces(detailQuery.data?.member_role)
  const pendingFaceId =
    (reference.isPending ? reference.variables?.faceId : null) ??
    (mainPhoto.isPending ? mainPhoto.variables : null) ??
    (evidence.isPending ? evidence.variables : null) ??
    null

  return {
    detail: detailQuery.data,
    state: {
      mainFaceId: resolveMainFaceId(detailQuery.data, chosenFaceId),
      pendingFaceId,
      isLoading: detailQuery.isLoading,
      errorKey: latestRefusal([
        { at: reference.submittedAt, key: confirmedFaceErrorKey(reference.error) },
        { at: mainPhoto.submittedAt, key: confirmedFaceErrorKey(mainPhoto.error) },
      ]),
    },
    actions: {
      // The face's evidence in the graph: a read, open to a viewer too.
      onShowInGraph: faceId => evidence.mutate(faceId),
      onToggleReference: canDecide
        ? (faceId, next) => reference.mutate({ faceId, reference: next })
        : undefined,
      onMakeMainPhoto: canDecide ? faceId => mainPhoto.mutate(faceId) : undefined,
    },
  }
}
